// src/pages/pentest/dashboard/exploitsModal.jsx
import React, { useMemo } from 'react';
import {
  Box,
  Card,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// ✅ icons for each state (hackery / agent vibe)
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';          // Suggestion
import TerminalIcon from '@mui/icons-material/Terminal';                // Commands
import VisibilityIcon from '@mui/icons-material/Visibility';            // Observation

import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';

// ---- helpers -----------------------------------------------------
const sevColor = {
  critical: '#800080',
  high: '#f44336',
  medium: '#ff9800',
  low: '#2196f3',
  info: '#4caf50',
  informational: '#4caf50',
};

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(String(text || ''));
  } catch {}
};

const parseLogMessages = (raw = '') => {
  const text = String(raw || '').replace(/\r\n/g, '\n').trim();
  if (!text) return [];

  const lines = text.split('\n');
  const items = [];
  let current = null;

  const push = () => {
    if (current && String(current.content || '').trim()) {
      items.push({ ...current, content: String(current.content).trim() });
    }
    current = null;
  };

  const start = (type, initial = '') => {
    push();
    current = { type, content: initial ? String(initial).trim() : '' };
  };

  for (const line of lines) {
    const m = line.match(/^(Suggestion|Commands|Commands Observation|Observation)\s*:\s*(.*)$/i);
    if (m) {
      const label = String(m[1]).toLowerCase();
      const rest = m[2] ?? '';
      if (label === 'suggestion') start('Suggestion', rest);
      else if (label === 'commands') start('Commands', rest);
      else start('Observation', rest);
    } else {
      if (!current) current = { type: 'Observation', content: '' };
      current.content += (current.content ? '\n' : '') + line;
    }
  }

  push();
  return items;
};

const extractCommands = (content = '') => {
  const s = String(content || '').trim();
  if (!s) return [];
  const out = [];
  const re = /'([^']*)'|"([^"]*)"/g;
  let m;
  while ((m = re.exec(s)) !== null) {
    const hit = (m[1] ?? m[2] ?? '').trim();
    if (hit) out.push(hit);
  }
  return out.length ? out : [s];
};

// ✅ requested colors + icons
const getLogTheme = (type) => {
  const t = String(type || '').toLowerCase();
  if (t === 'suggestion') {
    return {
      label: 'Suggestion',
      border: '#1976d2',                 // primary blue
      chipBg: 'rgba(25,118,210,0.18)',
      chipColor: '#1976d2',
      bg: 'rgba(25,118,210,0.06)',
      icon: <AutoAwesomeIcon fontSize="small" />,
    };
  }
  if (t === 'commands') {
    return {
      label: 'Commands',
      border: '#000000',                 // black
      chipBg: 'rgba(0,0,0,0.10)',
      chipColor: '#000000',
      bg: 'rgba(0,0,0,0.03)',
      icon: <TerminalIcon fontSize="small" />,
    };
  }
  return {
    label: 'Observation',
    border: '#2e7d32',                   // green
    chipBg: 'rgba(46,125,50,0.14)',
    chipColor: '#2e7d32',
    bg: 'rgba(46,125,50,0.06)',
    icon: <VisibilityIcon fontSize="small" />,
  };
};

// ---- component ---------------------------------------------------
const ExploitsModal = ({ open, handleClose, exploit, mode = 'report' }) => {
  const severity = String(exploit?.severity || '').toLowerCase();
  const sevBg = sevColor[severity] || '#9e9e9e';

  const title = exploit?.name || 'Exploit';

  const drawerWidth =
    mode === 'report'
      ? { xs: '94vw', sm: 720, md: 920 }
      : { xs: '94vw', sm: 560, md: 640 };

  const logItems = useMemo(
    () => parseLogMessages(exploit?.command_output || ''),
    [exploit?.command_output]
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: drawerWidth,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Chip
            size="small"
            label={(exploit?.severity || 'SEV').toUpperCase()}
            sx={{ bgcolor: sevBg, color: '#fff', fontWeight: 800 }}
          />
          <Typography variant="h6" sx={{ fontWeight: 900 }} noWrap title={title}>
            {mode === 'report' ? 'Report' : 'Agent Logs'} — {title}
          </Typography>
        </Stack>

        <Tooltip title="Close" arrow>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {!exploit ? (
        <Typography>No exploit selected.</Typography>
      ) : mode === 'report' ? (
        <>
          {/* Quick copy actions */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Tooltip title="Copy report markdown" arrow>
              <IconButton size="small" onClick={() => copy(exploit?.report_details || '')}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="text.secondary">
              Markdown report from <code>report_details</code>
            </Typography>
          </Stack>

          <Box sx={{ overflowY: 'auto', pr: 0.5 }}>
            <ReactMarkdown
              components={{
                code({ className, children, ...props }) {
                  const isBlock = className && className.startsWith('language-');
                  if (!isBlock) return <code {...props}>{children}</code>;
                  const language = className?.replace('language-', '') || 'text';
                  return (
                    <SyntaxHighlighter
                      language={language}
                      style={prism}
                      wrapLines
                      showLineNumbers
                      customStyle={{ borderRadius: 10, margin: 0, fontSize: '0.9rem' }}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  );
                },
              }}
            >
              {exploit?.report_details || 'No report details available.'}
            </ReactMarkdown>
          </Box>
        </>
      ) : (
        <>
          {/* Logs view */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Tooltip title="Copy full command_output" arrow>
              <IconButton size="small" onClick={() => copy(exploit?.command_output || '')}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="text.secondary">
              Parsed from <code>command_output</code> into Suggestion / Commands / Observation
            </Typography>
          </Stack>

          {/* ✅ Scrollable container (prevents overlap + allows long lists) */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              pr: 0.5,
              pb: 1,
            }}
          >
            <Stack spacing={1.5}>
              {logItems.length ? (
                logItems.map((item, idx) => {
                  const theme = getLogTheme(item.type);
                  const t = String(item.type || '').toLowerCase();

                  // Commands: render one or more code blocks (no truncation)
                  if (t === 'commands') {
                    const cmds = extractCommands(item.content);

                    return (
                      <Card
                        key={`${theme.label}-${idx}`}
                        variant="outlined"
                        sx={{
                          borderRadius: 2.5,
                          p: 1.5,
                          borderLeft: `6px solid ${theme.border}`,
                          bgcolor: theme.bg,
                          overflow: 'hidden',
                        }}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box sx={{ color: theme.chipColor, display: 'flex', alignItems: 'center' }}>
                              {theme.icon}
                            </Box>
                            <Chip
                              size="small"
                              label={theme.label}
                              sx={{
                                bgcolor: theme.chipBg,
                                color: theme.chipColor,
                                fontWeight: 900,
                                letterSpacing: 0.3,
                              }}
                            />
                          </Stack>

                          <Tooltip title="Copy commands" arrow>
                            <IconButton size="small" onClick={() => copy(cmds.join('\n'))}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>

                        <Stack spacing={1}>
                          {cmds.map((c, i) => (
                            <Box key={`${idx}-cmd-${i}`}>
                              <SyntaxHighlighter
                                language="bash"
                                style={prism}
                                wrapLines
                                customStyle={{
                                  borderRadius: 10,
                                  margin: 0,
                                  fontSize: '0.88rem',
                                }}
                              >
                                {String(c || '').trim()}
                              </SyntaxHighlighter>
                            </Box>
                          ))}
                        </Stack>
                      </Card>
                    );
                  }

                  // Suggestion / Observation: full text, scrollable page, no cutoff
                  return (
                    <Card
                      key={`${theme.label}-${idx}`}
                      variant="outlined"
                      sx={{
                        borderRadius: 2.5,
                        p: 1.5,
                        borderLeft: `6px solid ${theme.border}`,
                        bgcolor: theme.bg,
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ color: theme.chipColor, display: 'flex', alignItems: 'center' }}>
                            {theme.icon}
                          </Box>
                          <Chip
                            size="small"
                            label={theme.label}
                            sx={{
                              bgcolor: theme.chipBg,
                              color: theme.chipColor,
                              fontWeight: 900,
                              letterSpacing: 0.3,
                            }}
                          />
                        </Stack>

                        <Tooltip title={`Copy ${theme.label.toLowerCase()}`} arrow>
                          <IconButton size="small" onClick={() => copy(item.content)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>

                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          lineHeight: 1.55,
                        }}
                      >
                        {item.content}
                      </Typography>
                    </Card>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No logs available.
                </Typography>
              )}
            </Stack>
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default ExploitsModal;
